import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Icon from 'react-feather';
import * as Yup from 'yup';
import { toZonedTime } from 'date-fns-tz';

import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import {
  fileDownload,
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';

import {
  archiveOptionsGet,
  bothLegsReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import config from '../../../config';
import { useTimezone } from '../../../store/slices/commonSlice';

const BothLegsReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  // Starts true because the first search waits for the archive options to resolve -
  // without this the empty state flashes before the loader appears.
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [audioDetails, setAudioDetails] = useState(null);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const hasAutoSearchedRef = useRef(false);

  const today = toZonedTime(new Date(), timezone);
  const initialValues = {
    from_date: startOfDay(today),
    to_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    from_date: Yup.date().required('From date is required'),
    to_date: Yup.date().required('To date is required')
  });

  // `range` lets the first automatic search use the month range as soon as it is
  // derived, without waiting for setFieldValue to land in `values`.
  const fetchReport = async (range) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(range?.from_date || values.from_date);
      const formattedEndDate = formatPayloadDate(range?.to_date || values.to_date);

      const payload = {
        ...values,
        from_date: formattedStartDate,
        to_date: formattedEndDate
      };

      const response = await dispatch(bothLegsReportGet(payload)).unwrap();
      if (response) {
        setReportData(response);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const onSubmit = () => fetchReport();

  // Runs the load-time search exactly once, whichever path resolves the range first.
  const runInitialSearch = (range) => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    fetchReport(range);
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
        // No month options means no derived range, so fall back to the default dates.
        runInitialSearch();
      }
    } catch (error) {
      setArchiveOptions([]);
      runInitialSearch();
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
  }, []);

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  // The archive options land after mount, so the month range is only known here.
  // The first search has to wait for it, otherwise it fetches today's range while
  // the filter already shows the whole month.
  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('from_date', minDate);
      setFieldValue('to_date', maxDate);
      runInitialSearch({ from_date: minDate, to_date: maxDate });
    } else if (archiveOptions?.length > 0) {
      // Options loaded but the label gave no range - do not leave the first search pending.
      runInitialSearch();
    }
  }, [minDate?.getTime(), maxDate?.getTime(), archiveOptions?.length]);

  const handleAudioAction = (path, rowData, label) => {
    if (!path || path === '-') return null;
    return (
      <div className="d-flex gap-2 justify-content-center">
        <Icon.PlayCircle
          size={16}
          onClick={() =>
            setAudioDetails({
              ...rowData,
              src: `${config.BASE_URL}cdrs_report/download?filename=${path}`
            })
          }
          className="cursor-pointer text-info"
        />
        <Icon.Download
          size={16}
          onClick={() => {
            fileDownload(`${config.BASE_URL}cdrs_report/download?filename=${path}`);
          }}
          className="cursor-pointer"
        />
      </div>
    );
  };

  return (
    <div>
      <AdminHeader
        title="Both Legs Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isRefresh={reportData?.data?.length > 0}
        onRefreshClick={() => onSubmit()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="4" md="4" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="4" md="4" className="flex-row">
                <FormDateInput
                  name="from_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.to_date || maxDate}
                  value={values.from_date}
                  onChange={(e) => setFieldValue('from_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="4" md="4" className="flex-row">
                <FormDateInput
                  name="to_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.from_date || minDate}
                  maxDate={maxDate}
                  value={values.to_date}
                  onChange={(e) => setFieldValue('to_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData.data?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    const isRecordingCol = val?.name.includes('recording_path');
                    return (
                      <th
                        key={index}
                        className={isRecordingCol ? 'text-center' : ''}
                        style={isRecordingCol ? { width: '150px' } : {}}>
                        {val.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        const isRecordingCol = value?.name.includes('recording_path');
                        if (isRecordingCol) {
                          return (
                            <td key={idx} className="text-center">
                              {handleAudioAction(val[value?.name], val, value?.label) || '-'}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </div>
  );
};

export default BothLegsReport;
